import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import ContactForm from "@/components/forms/ContactForm";
import AppointmentForm from "@/components/forms/AppointmentForm";
import { SITE } from "@/lib/site";

const CONTACT_CARDS = [
  { icon: MapPin, label: "Office", value: SITE.address, testId: "contact-card-office" },
  { icon: Phone, label: "Phone", value: SITE.phone, href: SITE.phoneHref, testId: "contact-card-phone" },
  { icon: Mail, label: "Email", value: SITE.email, href: SITE.emailHref, testId: "contact-card-email" },
  { icon: Clock, label: "Hours", value: SITE.hours, testId: "contact-card-hours" },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Maritime Thermal Solutions | Halifax, Nova Scotia"
        description="Contact Maritime Thermal Solutions for appointment questions, representative verification, or general inquiries across Nova Scotia."
      />

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-x py-14 md:py-20">
          <p className="eyebrow">Contact</p>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold leading-tight text-brand-navy sm:text-5xl" data-testid="contact-heading">
            Contact Maritime Thermal Solutions
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Get in touch with our Halifax-based team. We're happy to answer general questions,
            confirm or change an appointment, or help verify a representative.
          </p>
        </div>
      </section>

      <section className="section-y" data-testid="contact-info-grid">
        <div className="container-x">
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CONTACT_CARDS.map((c) => (
              <li
                key={c.label}
                className="rounded-lg border border-slate-200 bg-white p-6"
                data-testid={c.testId}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-brand-navy/5 text-brand-navy">
                  <c.icon size={18} aria-hidden="true" />
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
                {c.href ? (
                  <a href={c.href} className="mt-1 block text-sm font-medium text-slate-900 hover:text-brand-navy break-all">
                    {c.value}
                  </a>
                ) : (
                  <p className="mt-1 text-sm font-medium text-slate-900">{c.value}</p>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-slate-500">
            <strong className="font-semibold text-slate-700">Service area:</strong> {SITE.serviceArea}.
          </p>
        </div>
      </section>

      <section className="section-y bg-slate-50" data-testid="contact-general-form-section">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Send us a message
            </h2>
            <p className="mt-3 text-slate-600">
              General questions, appointment confirmations, representative verification,
              or any concerns — we'll get back to you.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <section className="section-y" data-testid="contact-appointment-form-section">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Confirm or change an appointment
            </h2>
            <p className="mt-3 text-slate-600">
              Already booked a time with us? Use this form to confirm, reschedule, or cancel.
              You can also call us at <a href={SITE.phoneHref} className="font-semibold text-brand-navy hover:underline">{SITE.phone}</a>.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
              <AppointmentForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
